import { Icon } from "@twilight-toolkit/ui"
import type { GetStaticProps } from "next"
import Head from "next/head"
import Image from "next/image"
import { useRouter } from "next/router"

import { pageLayout } from "~/components/Page"
import { getPosts, type LocalPost } from "~/content/posts"
import type { NextPageWithLayout } from "~/pages/_app"
import { trimStr } from "~/utilities/string"
import {
	getViewTransitionName,
	navigateWithTransition,
} from "~/utilities/viewTransition"

interface Props {
	friends: LocalPost[]
}

const Links: NextPageWithLayout<Props> = ({ friends }) => {
	const router = useRouter()

	return (
		<div>
			<Head>
				<link
					rel="icon"
					href="data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>🧑‍🤝‍🧑</text></svg>"
				/>
			</Head>
			<section className="mt-0 pt-24 lg:mt-20 lg:pt-0">
				<div className="mb-4 flex items-center">
					<div className="flex flex-1 items-center">
						<div className="mt-1 mr-4.5 flex -rotate-6 cursor-pointer items-center">
							<span className="text-[35px] drop-shadow-lg hover:animate-spin">
								🔗
							</span>
						</div>
						<div>
							<h1 className="flex items-center gap-x-1.5 text-[28px] font-medium tracking-wide whitespace-nowrap text-black dark:text-white">
								<span
									style={{
										viewTransitionName: getViewTransitionName("Links"),
									}}>
									Links
								</span>
							</h1>
							<p className="-mt-1 text-sm text-neutral-500 dark:text-gray-400">
								A collection of fun stuff and links from the internet.
							</p>
						</div>
					</div>
					<div className="mt-2 flex h-full items-center justify-end whitespace-nowrap">
						<div className="flex-1 pr-2 pl-5">
							<p className="text-xl text-gray-500 dark:text-gray-400">
								<button
									type="button"
									onClick={() => navigateWithTransition(router, "/pages")}
									className="flex cursor-pointer items-center">
									<span className="mr-2 h-6 w-6">
										<Icon name="left" />
									</span>
									Pages
								</button>
							</p>
						</div>
					</div>
				</div>
			</section>
			<div className="my-5">
				<hr className="dark:border-gray-600" />
			</div>
			<section className="mb-10">
				<h2 className="inline-flex items-center rounded-tl-xl rounded-tr-xl border border-gray-300 bg-white px-4 pt-[4px] pb-1 font-medium tracking-wider shadow-xs dark:border-gray-600 dark:bg-gray-700">
					<span className="mr-1.5 flex h-5 w-5 text-blue-500">
						<Icon name="pencilTool" />
					</span>
					<span className="uppercase">Tools</span>
				</h2>
				<div className="my-2 mb-4 flex w-full items-center rounded-br-xl rounded-bl-xl border border-gray-300 bg-white px-4 py-3 shadow-xs dark:border-gray-600 dark:bg-gray-700">
					<p className="items-center text-xl tracking-wide text-neutral-500 dark:text-gray-300">
						With a high bar for build quality, aesthetic, and usability. Here
						are some of the indie tools that I use or researched.
					</p>
				</div>
				<div className="mt-5 grid grid-cols-2 gap-4" data-cy="toolsItems">
					<div className="z-40 flex w-full cursor-pointer flex-col rounded-md border bg-white shadow-xs transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:shadow-none">
						<a
							href="https://buttergrow.com"
							target="_blank"
							rel="noreferrer"
							className="w-full flex-1 items-center px-6 py-4">
							<h3 className="mb-0.5 flex items-center text-2xl font-medium tracking-wide">
								<Image
									alt="ButterGrow"
									src="https://static.ouorz.com/buttergrow_logo.webp"
									width={20}
									height={20}
									className="rounded-full border border-gray-200 dark:border-gray-500"
									loading="lazy"
								/>
								<span className="ml-2">ButterGrow</span>
							</h3>
							<p
								className="overflow-hidden text-4 tracking-wide text-ellipsis whitespace-nowrap text-gray-500 dark:text-gray-400"
								dangerouslySetInnerHTML={{
									__html: trimStr(
										"The AI Growth Agency based on OpenClaw",
										150
									),
								}}
							/>
						</a>
					</div>
					<div className="z-40 flex w-full cursor-pointer flex-col rounded-md border bg-white shadow-xs transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:shadow-none">
						<a
							href="https://github.com/ttttonyhe/autogrind"
							target="_blank"
							rel="noreferrer"
							className="w-full flex-1 items-center px-6 py-4">
							<h3 className="mb-0.5 flex items-center text-2xl font-medium tracking-wide">
								<Image
									alt="Autogrind"
									src="https://static.ouorz.com/autogrind-logo.png"
									width={20}
									height={20}
									className="rounded-full border border-gray-200 dark:border-gray-500"
									loading="lazy"
								/>
								<span className="ml-2">Autogrind</span>
							</h3>
							<p
								className="overflow-hidden text-4 tracking-wide text-ellipsis whitespace-nowrap text-gray-500 dark:text-gray-400"
								dangerouslySetInnerHTML={{
									__html: trimStr("24x7 auto-work mode for your agents", 150),
								}}
							/>
						</a>
					</div>
				</div>
			</section>
			<div className="mb-10">
				<hr className="dark:border-gray-600" />
			</div>
			<section className="mb-10">
				<h2 className="inline-flex items-center rounded-tl-xl rounded-tr-xl border border-gray-300 bg-white px-4 pt-[4px] pb-1 font-medium tracking-wider shadow-xs dark:border-gray-600 dark:bg-gray-700">
					<span className="mr-1.5 flex h-5 w-5 text-orange-500">
						<Icon name="people" />
					</span>
					<span className="uppercase">Webring</span>
				</h2>
				<div className="my-2 mb-4 flex w-full items-center rounded-br-xl rounded-bl-xl border border-gray-300 bg-white px-4 py-3 shadow-xs dark:border-gray-600 dark:bg-gray-700">
					<p className="items-center text-xl tracking-wide text-neutral-500 dark:text-gray-300">
						To join this webring, email me at ABC_tony.hlp@hotmail.com (with the
						leading &ldquo;ABC_&rdquo; removed).
					</p>
				</div>
				<div className="mt-5 grid grid-cols-2 gap-4" data-cy="friendsItems">
					{friends.length > 0 ? (
						friends.map((item) => {
							return (
								<div
									className="z-40 flex w-full cursor-pointer flex-col rounded-md border bg-white shadow-xs transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:shadow-none"
									key={item.post_metas.link}>
									<a
										href={item.post_metas.link}
										target="_blank"
										rel="noreferrer"
										className="w-full flex-1 items-center px-6 py-4">
										<h3 className="mb-0.5 flex items-center text-2xl font-medium tracking-wide">
											<Image
												alt={item.post_title}
												src={item.post_img.url}
												width={20}
												height={20}
												className="rounded-full border border-gray-200 dark:border-gray-500"
												loading="lazy"
											/>
											<span className="ml-2">{item.post_title}</span>
										</h3>
										<p
											className="overflow-hidden text-4 tracking-wide text-ellipsis whitespace-nowrap text-gray-500 dark:text-gray-400"
											dangerouslySetInnerHTML={{
												__html: trimStr(item.post_excerpt.four, 150),
											}}
										/>
									</a>
								</div>
							)
						})
					) : (
						<div className="col-span-2 rounded-md border border-gray-200 bg-white px-6 py-8 text-center dark:border-gray-700 dark:bg-gray-800">
							<p className="text-gray-500 dark:text-gray-400">
								No webring links available at the moment.
							</p>
						</div>
					)}
				</div>
			</section>
		</div>
	)
}

Links.layout = pageLayout

export const getStaticProps: GetStaticProps<Props> = () => ({
	props: { friends: getPosts({ cate: 2 }) },
})

export default Links
